import React, { useState } from "react";
import { api } from "../../lib/api";
import { INDUSTRY_NAMES } from "../../lib/industries";
import { FieldIn, FieldSel, FieldArea, Modal } from "./Fields";
import { toast } from "sonner";

const EMPTY = {
  title: "", company_name: "", client_name: "", location: "", industry: INDUSTRY_NAMES[0], department: "",
  employment_type: "full_time", vacancies: "", experience_min: 0, experience_max: 5,
  salary_min: "", salary_max: "", qualification: "", description: "",
  responsibilities: [], requirements: [], skills: [], status: "active",
};
const EMP = [{ v: "full_time", l: "Full-time" }, { v: "contract", l: "Contract" }, { v: "part_time", l: "Part-time" }, { v: "internship", l: "Internship" }];
const STATUS = [{ v: "active", l: "Active" }, { v: "on_hold", l: "On Hold" }, { v: "closed", l: "Closed" }];
const lines = (v) => Array.isArray(v) ? v : v.split("\n").map(s => s.trim()).filter(Boolean);
const num = (v) => (v === "" || v === null || v === undefined ? null : Number(v));

export default function JobFormModal({ job, onClose, onSaved }) {
  const [j, setJ] = useState(job ? { ...EMPTY, ...job } : EMPTY);
  const [saving, setSaving] = useState(false);
  const set = (k) => (v) => setJ({ ...j, [k]: v });

  const save = async () => {
    if (!j.title || !j.company_name || !j.location || !j.description) return toast.error("Title, company, location and description are required");
    setSaving(true);
    const payload = { ...j,
      responsibilities: lines(j.responsibilities), requirements: lines(j.requirements),
      skills: Array.isArray(j.skills) ? j.skills : j.skills.split(",").map(s => s.trim()).filter(Boolean),
      experience_min: Number(j.experience_min) || 0, experience_max: Number(j.experience_max) || 0,
      salary_min: num(j.salary_min), salary_max: num(j.salary_max), vacancies: num(j.vacancies),
    };
    try {
      if (job) await api.put(`/jobs/${job.id}`, payload); else await api.post("/jobs", payload);
      toast.success(job ? "Job updated" : "Job published");
      onSaved();
    } catch (e) { toast.error(e.response?.data?.detail || "Save failed"); } finally { setSaving(false); }
  };

  return (
    <Modal title={job ? "Edit Job" : "Post a New Job"} onClose={onClose} wide testid="job-form-modal">
      <div className="space-y-4">
        <div className="grid md:grid-cols-3 gap-4">
          <FieldIn label="Job Title" required val={j.title} onChange={set("title")} tid="jf-title" />
          <FieldIn label="Company (shown publicly)" required val={j.company_name} onChange={set("company_name")} tid="jf-company" placeholder="e.g., Leading Pharma Co." />
          <FieldIn label="Client Name (private)" val={j.client_name} onChange={set("client_name")} tid="jf-client" placeholder="Internal only" />
          <FieldIn label="Location" required val={j.location} onChange={set("location")} tid="jf-location" />
          <FieldSel label="Industry" val={j.industry} onChange={set("industry")} opts={INDUSTRY_NAMES} tid="jf-industry" />
          <FieldIn label="Department" val={j.department} onChange={set("department")} tid="jf-dept" />
          <FieldSel label="Employment Type" val={j.employment_type} onChange={set("employment_type")} opts={EMP} tid="jf-type" />
          <FieldIn label="Vacancies" type="number" val={j.vacancies} onChange={set("vacancies")} tid="jf-vacancies" />
          <FieldSel label="Job Status" val={j.status} onChange={set("status")} opts={STATUS} tid="jf-status" />
          <FieldIn label="Min Exp (yrs)" type="number" val={j.experience_min} onChange={set("experience_min")} tid="jf-emin" />
          <FieldIn label="Max Exp (yrs)" type="number" val={j.experience_max} onChange={set("experience_max")} tid="jf-emax" />
          <FieldIn label="Qualification" val={j.qualification} onChange={set("qualification")} tid="jf-qual" />
          <FieldIn label="Salary Min (₹ p.a.)" type="number" val={j.salary_min} onChange={set("salary_min")} tid="jf-smin" />
          <FieldIn label="Salary Max (₹ p.a.)" type="number" val={j.salary_max} onChange={set("salary_max")} tid="jf-smax" />
          <FieldIn label="Skills (comma-separated)" val={Array.isArray(j.skills) ? j.skills.join(", ") : j.skills} onChange={set("skills")} tid="jf-skills" />
        </div>
        <FieldArea label="Description *" val={j.description} onChange={set("description")} rows={4} tid="jf-desc" />
        <div className="grid md:grid-cols-2 gap-4">
          <FieldArea label="Responsibilities (one per line)" rows={4} val={Array.isArray(j.responsibilities) ? j.responsibilities.join("\n") : j.responsibilities} onChange={set("responsibilities")} tid="jf-resp" />
          <FieldArea label="Requirements (one per line)" rows={4} val={Array.isArray(j.requirements) ? j.requirements.join("\n") : j.requirements} onChange={set("requirements")} tid="jf-req" />
        </div>
        <div className="flex gap-2 pt-2">
          <button onClick={onClose} className="ae-btn-outline text-sm flex-1" data-testid="jf-cancel">Cancel</button>
          <button onClick={save} disabled={saving} className="ae-btn-primary text-sm flex-1" data-testid="jf-submit">
            {saving ? "Saving…" : job ? "Save Changes" : "Publish Job"}
          </button>
        </div>
      </div>
    </Modal>
  );
}
